<template>
  <div class="p-4 text-center">
    <div class="row">
      <div class="col-sm-6">
        <!-- Scanner Section -->
        <div class="scanner-container">
          <h2 class="scan-title">📱 Scan QR Code Here</h2>
          
          <!-- Camera Frame -->
          <div class="camera-frame">
            <div class="frame-corner top-left"></div>
            <div class="frame-corner top-right"></div>
            <div class="frame-corner bottom-left"></div>
            <div class="frame-corner bottom-right"></div>
            
            <!-- Scanning Line Animation -->
            <div class="scanning-line"></div>
            
            <!-- Camera Preview -->
            <div id="qr-reader" class="qr-reader-container"></div>
            
            <!-- Camera Icon Overlay (shown when camera is loading) -->
            <div class="camera-overlay">
              <div class="camera-icon">📷</div>
              <p class="loading-text">Initializing Camera...</p>
            </div>
          </div>
          
          <p class="scan-instruction">Position QR code within the frame</p>
        </div>
      </div>
      
      <div class="col-sm-6">
        <!-- Status Display -->
        <div v-if="client" class="card shadow-lg client-card" :class="{'success-animation': client.subscription_active}">
          <div class="card-body" :class="client.subscription_active ? 'bg-success text-white' : 'bg-danger text-white'">
            
            <!-- Success Animation Overlay -->
            <div v-if="client.subscription_active" class="success-overlay">
              <div class="checkmark-animation">✓</div>
              <div class="success-particles">
                <div class="particle"></div>
                <div class="particle"></div>
                <div class="particle"></div>
                <div class="particle"></div>
                <div class="particle"></div>
              </div>
            </div>
            
            <h2 class="card-title text-4xl font-bold mb-4 slide-in">👤 {{ client.name }}</h2>
            
            <!-- Subscription Status -->
            <div class="mb-4 slide-in-delay-1" style="font-size:2.5rem" v-if="client.subscription_active">
                {{checkinOutResponse.check_out == null ? $t('check_in_success') : $t('check_out_success')}}
            </div>

            <!-- Days Remaining -->
            <div class="mb-4 slide-in-delay-2" v-if="client.subscription_active">
              <div class="days-container">
              <p class="text-2xl font-bold mb-2">⏳ Days Remaining:</p>
                <p class="remaining-days bounce-in">
                  {{ client.days_remaining }}
                  <span class="text-3xl">day(s)</span>
                </p>
                <div class="days-progress-ring">
                  <svg class="progress-ring" width="120" height="120">
                    <circle
                      class="progress-ring-circle"
                      stroke="rgba(255,255,255,0.3)"
                      stroke-width="8"
                      fill="transparent"
                      r="52"
                      cx="60"
                      cy="60"/>
                    <circle
                      class="progress-ring-circle progress-ring-fill"
                      stroke="white"
                      stroke-width="8"
                      fill="transparent"
                      r="52"
                      cx="60"
                      cy="60"
                      :style="progressStyle"/>
                  </svg>
                </div>
              </div>
            </div>
            <div v-else>
              <p class="text-2xl font-bold mb-2">🚫 No Active Subscription</p>
              <p class="text-xl">Please contact the gym staff for assistance.</p>
            </div>

            <!-- Last Attendance -->
            <div class="mb-4 slide-in-delay-3" v-if="client.subscription_active">
              <p class="text-2xl font-bold mb-2">📅 Last Visit:</p>
              <p class="text-2xl">
                {{ client.last_attendance ? new Date(client.last_attendance).toLocaleString() : 'No previous visits' }}
              </p>
            </div>

          </div>
        </div>

        <!-- Error -->
        <div v-if="error" class="alert alert-danger mt-4 text-xl shake-animation">⚠️ {{ error }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Html5Qrcode } from "html5-qrcode";
import helper from '../helper'

export default {
  name: "GymAttendanceScanner",
  data() {
    return {
      client: null,
      error: null,
      scannedAt: null,
      isScanning: false,
      previsouScanned : null,
      checkinOutResponse : null
    };
  },
  computed: {
    progressStyle() {
      if (!this.client || !this.client.subscription_active) return '';
      
      // Assuming 30 days max subscription
      const maxDays = 30;
      const percentage = (this.client.days_remaining / maxDays) * 100;
      const circumference = 2 * Math.PI * 52;
      const strokeDashoffset = circumference - (percentage / 100) * circumference;
      
      return {
        strokeDasharray: circumference,
        strokeDashoffset: strokeDashoffset,
        transition: 'stroke-dashoffset 2s ease-in-out'
      };
    }
  },
  mounted() {
    window.mixpanel.track("external_attendance");
    //check permissions
    if(helper.checkAccess('view_attendance') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_attendance') == -2) return this.$router.push({path : '/subscribe'})

    this.initializeScanner();
  },
  methods: {
    async initializeScanner() {
      try {
        const html5QrCode = new Html5Qrcode("qr-reader");
        
        // Hide camera overlay once scanner starts
        setTimeout(() => {
          const overlay = document.querySelector('.camera-overlay');
          if (overlay) {
            overlay.style.opacity = '0';
            overlay.style.pointerEvents = 'none';
          }
        }, 2000);

        await html5QrCode.start(
          { facingMode: "environment" },
          { fps: 10 },
          this.onScanSuccess,
          this.onScanFailure
        );
        
        this.isScanning = true;
      } catch (err) {
        this.error = "Unable to start scanner: " + err;
      }
    },
    shortBase64ToNumber(base64) {
      const binary = atob(base64);
      let num = 0;
      for (let i = 0; i < binary.length; i++) {
        num = (num << 8) | binary.charCodeAt(i);
      }
      return num;
    },
    async onScanSuccess(decodedText) {
      try {
        this.error = null;
        if(this.previsouScanned === decodedText) return;
        // Stop the scanner to prevent multiple scans
        this.previsouScanned = decodedText;
        // Add scan feedback
        this.addScanFeedback();

        // Assume decodedText is just the client_id
        const response = await helper.customGet(`${process.env.VUE_APP_API}/subscriptions/clients/${this.shortBase64ToNumber(decodedText)}`);

        this.client = response.data;
        this.scannedAt = new Date().toLocaleTimeString();

        // Play success sound effect (optional)
        if (this.client.subscription_active) {
          this.playSuccessSound();
          // Mark attendance if client has an active subscription
          const response = await helper.customPost(`${process.env.VUE_APP_API}/attendance`, {
            clientId: this.client.id,
            checkIn: new Date().toISOString(),
            notes: 'FROM EXTERNAL SCANNER'
          }, 
          { showToast: false });
          this.checkinOutResponse = response.data;
          // if(response.data.check_out == null){
          //    window.vueOpener.open({
          //     message: window._i18n.t('check_in_success'),
          //     type: 'success',
          //     position : 'bottom',
          //   });
          // }else{
          //   window.vueOpener.open({
          //     message: window._i18n.t('check_out_success'),
          //     type: 'success',
          //     position : 'bottom',
          //   });
          // }
        }

        setTimeout(() => {
          this.client = null; // Clear client info after a short delay
          this.previsouScanned = null;
        }, 5000); // Clear after 5 seconds

        this.isScanning = false; // Stop scanning after a successful scan

      } catch (err) {
        this.error = "Failed to fetch client info or mark attendance.";
      }
    },
    
    onScanFailure() {
    },
    
    addScanFeedback() {
      const frame = document.querySelector('.camera-frame');
      if (frame) {
        frame.classList.add('scan-success');
        setTimeout(() => {
          frame.classList.remove('scan-success');
        }, 500);
      }
    },
    
    playSuccessSound() {
      // Create a simple beep sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    }
  },
};
</script>

<style scoped>
.scanner-container {
  position: relative;
  max-width: 400px;
  margin: 0 auto;
}

.scan-title {
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.camera-frame {
  position: relative;
  width: 100%;
  max-width: 350px;
  height: 350px;
  margin: 0 auto 1rem;
  border: 3px solid #007bff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 123, 255, 0.3);
  background: linear-gradient(135deg, #303030 0%, #303030 100%);
}

.camera-frame.scan-success {
  border-color: #28a745;
  box-shadow: 0 0 20px rgba(40, 167, 69, 0.6);
  animation: scanPulse 0.5s ease-out;
}

@keyframes scanPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}

.frame-corner {
  position: absolute;
  width: 30px;
  height: 30px;
  border: 4px solid #fff;
  z-index: 10;
}

.frame-corner.top-left {
  top: 10px;
  left: 10px;
  border-right: none;
  border-bottom: none;
  border-top-left-radius: 10px;
}

.frame-corner.top-right {
  top: 10px;
  right: 10px;
  border-left: none;
  border-bottom: none;
  border-top-right-radius: 10px;
}

.frame-corner.bottom-left {
  bottom: 10px;
  left: 10px;
  border-right: none;
  border-top: none;
  border-bottom-left-radius: 10px;
}

.frame-corner.bottom-right {
  bottom: 10px;
  right: 10px;
  border-left: none;
  border-top: none;
  border-bottom-right-radius: 10px;
}

.scanning-line {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, #00ff00, transparent);
  transform: translateX(-50%);
  animation: scanLine 2s ease-in-out infinite;
  z-index: 5;
}

@keyframes scanLine {
  0%, 100% { transform: translateX(-50%) translateY(-100%); }
  50% { transform: translateX(-50%) translateY(100%); }
}

.qr-reader-container {
  width: 100%;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
}

.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 15;
  transition: opacity 0.5s ease;
}

.camera-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  animation: pulse 2s ease-in-out infinite;
}

.loading-text {
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
}

.scan-instruction {
  color: #666;
  font-size: 1rem;
  margin-top: 1rem;
}

/* Client Card Animations */
.client-card {
  border-radius: 1rem;
  overflow: hidden;
  position: relative;
}

.success-animation {
  animation: cardSuccess 0.8s ease-out;
}

@keyframes cardSuccess {
  0% { transform: scale(0.9) rotateY(-10deg); opacity: 0; }
  50% { transform: scale(1.05) rotateY(5deg); }
  100% { transform: scale(1) rotateY(0deg); opacity: 1; }
}

.success-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
}

.checkmark-animation {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 3rem;
  color: white;
  animation: checkmarkBounce 1s ease-out;
}

@keyframes checkmarkBounce {
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(180deg); opacity: 1; }
  100% { transform: scale(1) rotate(360deg); opacity: 1; }
}

.success-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.particle {
  position: absolute;
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: particleFloat 2s ease-out infinite;
}

.particle:nth-child(1) { top: 20%; left: 20%; animation-delay: 0s; }
.particle:nth-child(2) { top: 30%; right: 20%; animation-delay: 0.2s; }
.particle:nth-child(3) { top: 60%; left: 30%; animation-delay: 0.4s; }
.particle:nth-child(4) { bottom: 30%; right: 30%; animation-delay: 0.6s; }
.particle:nth-child(5) { bottom: 20%; left: 50%; animation-delay: 0.8s; }

@keyframes particleFloat {
  0% { transform: translateY(0) scale(0); opacity: 1; }
  100% { transform: translateY(-100px) scale(1); opacity: 0; }
}

.slide-in {
  animation: slideIn 0.6s ease-out;
}

.slide-in-delay-1 {
  animation: slideIn 0.6s ease-out 0.2s both;
}

.slide-in-delay-2 {
  animation: slideIn 0.6s ease-out 0.4s both;
}

.slide-in-delay-3 {
  animation: slideIn 0.6s ease-out 0.6s both;
}

@keyframes slideIn {
  from { transform: translateX(-30px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

.status-badge {
  padding: 0.5rem 1rem;
  border-radius: 25px;
  display: inline-block;
  text-shadow: 0 2px 4px rgba(0,0,0,0.3);
}

.pulse-success {
  animation: pulseSuccess 2s ease-in-out infinite;
}

.pulse-danger {
  animation: pulseDanger 2s ease-in-out infinite;
}

@keyframes pulseSuccess {
  0%, 100% { box-shadow: 0 0 10px rgba(40, 167, 69, 0.5); }
  50% { box-shadow: 0 0 20px rgba(40, 167, 69, 0.8); }
}

@keyframes pulseDanger {
  0%, 100% { box-shadow: 0 0 10px rgba(220, 53, 69, 0.5); }
  50% { box-shadow: 0 0 20px rgba(220, 53, 69, 0.8); }
}

.days-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
}

.remaining-days {
  color: white;
  font-size: 4rem;
  font-weight: bold;
  text-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

.bounce-in {
  animation: bounceIn 1s ease-out;
}

@keyframes bounceIn {
  0% { transform: scale(0.3); opacity: 0; }
  50% { transform: scale(1.1); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
}

.days-progress-ring {
  position: relative;
}

.progress-ring {
  transform: rotate(-90deg);
}

.progress-ring-circle {
  transition: stroke-dashoffset 2s ease-in-out;
}

.card-body {
  padding: 2rem;
  position: relative;
}

.bg-success {
  background: linear-gradient(135deg, #28a745, #20c997);
}

.bg-danger {
  background: linear-gradient(135deg, #dc3545, #fd7e14);
}

.alert-danger {
  background: linear-gradient(135deg, #dc3545, #fd7e14);
  color: white;
  padding: 1rem;
  border-radius: 0.5rem;
  border: none;
}

.shake-animation {
  animation: shake 0.5s ease-in-out;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* QR Reader Specific Styles */
#qr-reader video {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover;
  border-radius: 16px;
}

#qr-reader {
  width: 100%;
  height: 100%;
}

/* Responsive Design */
@media (max-width: 768px) {
  .camera-frame {
    max-width: 280px;
    height: 280px;
  }
  
  .scan-title {
    font-size: 1.5rem;
  }
  
  .remaining-days {
    font-size: 3rem;
  }
  
  .days-container {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>